"use client";

import React from "react";
import { Phone, Mail, ArrowRight, MapPin, ShieldCheck, Clock } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
export default function FinalCta() {
  const shouldReduceMotion = useReducedMotion();
  const nearbyAreas = [{
    name: "Marlow",
    to: "/AreasWeCoverPage"
  }, {
    name: "Beaconsfield",
    to: "/AreasWeCoverPage"
  }, {
    name: "Bracknell",
    to: "/AreasWeCoverPage"
  }, {
    name: "Windsor",
    to: "/AreasWeCoverPage"
  }, {
    name: "Maidenhead",
    to: "/AreasWeCoverPage"
  }, {
    name: "Slough",
    to: "/AreasWeCoverPage"
  }];
  const benefits = [{
    title: "Direct Estimating Team",
    desc: "Fast turnaround on residential and commercial site assessments"
  }, {
    title: "Certified Safety",
    desc: "NASC and CHAS accredited contractors"
  }, {
    title: "Full South East Coverage",
    desc: "Dedicated operations across Berkshire, Surrey, Bucks, and London"
  }];
  return <section id="finalcta" data-nav="light" className="bg-background py-16 md:py-24 border-t border-border">
      <div className="container mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="rounded-2xl bg-secondary text-secondary-foreground p-8 md:p-12 lg:p-16 shadow-xl relative overflow-hidden">
          {/* Subtle background decorative shapes in theme colors */}
          <div aria-hidden="true" className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
          <div aria-hidden="true" className="absolute -left-24 -bottom-24 w-96 h-96 rounded-full bg-primary/15 blur-3xl pointer-events-none" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            {/* Main CTA copy */}
            <div className="lg:col-span-7 space-y-6">
              <div className="flex flex-wrap gap-2 items-center">
                <Badge variant="outline" className="bg-secondary-foreground/10 text-secondary-foreground border-secondary-foreground/20 px-3 py-1 text-xs font-medium uppercase tracking-wide">
                  Commercial & Residential Access
                </Badge>
                <span className="inline-flex items-center gap-1 text-xs text-secondary-foreground/80 font-medium">
                  <ShieldCheck className="size-3.5 text-primary" />
                  NASC & CHAS Accredited
                </span>
              </div>

              <h2 className="text-3xl sm:text-4xl md:text-5xl font-default font-bold text-secondary-foreground tracking-tight">
                Request a scaffolding quote
              </h2>

              <p className="text-base sm:text-lg text-secondary-foreground/85 max-w-xl leading-relaxed">
                Speak directly with our contracts team for reliable specifications, site visits, and clear estimates across the South East and Home Counties.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-md text-base px-8 h-12">
                  <Link to="/ContactRequestQuotePage" className="inline-flex items-center justify-center gap-2">
                    Request a quote
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg" className="bg-transparent border-secondary-foreground/30 text-secondary-foreground hover:bg-secondary-foreground/10 hover:text-secondary-foreground focus-visible:text-secondary-foreground h-12 px-6">
                  <a href="tel:01344929900" className="inline-flex items-center justify-center gap-2 text-secondary-foreground">
                    <Phone className="size-4 text-primary" />
                    <span className="text-secondary-foreground">01344 929900</span>
                  </a>
                </Button>
              </div>

              {/* Feature pillars */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6 border-t border-secondary-foreground/15">
                {benefits.map((item, i) => <div key={item.title} data-index={i} className="space-y-1">
                    <p className="text-sm font-semibold text-secondary-foreground">
                      {item.title}
                    </p>
                    <p className="text-xs text-secondary-foreground/75 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>)}
              </div>
            </div>

            {/* Direct contact & office quick card */}
            <div className="lg:col-span-5">
              <Card className="bg-card text-card-foreground shadow-lg border-border/80">
                <CardContent className="p-6 sm:p-8 space-y-6">
                  <div className="space-y-1">
                    <h3 className="text-xl font-bold font-default text-card-foreground">
                      Direct contact
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Get in touch directly with our commercial desk.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <a href="tel:01344929900" className="flex items-center gap-4 p-3.5 rounded-lg border border-border bg-background hover:bg-accent text-foreground hover:text-foreground transition-[background-color,border-color] group">
                      <div className="size-10 rounded-md bg-primary/10 flex items-center justify-center text-primary shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                        <Phone className="size-5" />
                      </div>
                      <div className="min-w-0">
                        <span className="block text-xs font-medium text-muted-foreground group-hover:text-muted-foreground">
                          Telephone
                        </span>
                        <span className="block text-base font-semibold text-foreground group-hover:text-foreground truncate">
                          01344 929900
                        </span>
                      </div>
                    </a>

                    <a href="mailto:quotes@bmfscaffolding.co.uk" className="flex items-center gap-4 p-3.5 rounded-lg border border-border bg-background hover:bg-accent text-foreground hover:text-foreground transition-[background-color,border-color] group">
                      <div className="size-10 rounded-md bg-primary/10 flex items-center justify-center text-primary shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                        <Mail className="size-5" />
                      </div>
                      <div className="min-w-0">
                        <span className="block text-xs font-medium text-muted-foreground group-hover:text-muted-foreground">
                          Estimating Email
                        </span>
                        <span className="block text-base font-semibold text-foreground group-hover:text-foreground truncate">
                          quotes@bmfscaffolding.co.uk
                        </span>
                      </div>
                    </a>
                  </div>

                  <div className="p-3.5 rounded-lg bg-muted/60 text-xs text-muted-foreground space-y-1.5">
                    <div className="flex items-center gap-1.5 font-medium text-foreground">
                      <Clock className="size-3.5 text-primary" />
                      <span>Operational Hours</span>
                    </div>
                    <p>Monday to Friday: 07:00 – 17:30</p>
                    <p>Emergency response & weekend works by prior arrangement.</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </motion.div>

        {/* Nearby areas text links list */}
        <div className="mt-12 pt-8 border-t border-border">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <MapPin className="size-4 text-primary shrink-0" />
              <span>Nearby areas covered:</span>
            </div>

            <nav aria-label="Nearby scaffolding service locations" className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
              {nearbyAreas.map((area, i) => <div key={area.name} data-index={i} className="flex items-center gap-4">
                  <Link to={area.to} className="text-muted-foreground hover:text-primary transition-colors font-medium hover:underline underline-offset-4">
                    {area.name}
                  </Link>
                  {i < nearbyAreas.length - 1 && <Separator orientation="vertical" className="h-3 bg-border hidden sm:block" />}
                </div>)}
              <Link to="/AreasWeCoverPage" className="text-primary font-semibold hover:underline inline-flex items-center gap-1 text-sm ml-2">
                View all areas
                <ArrowRight className="size-3.5" />
              </Link>
            </nav>
          </div>
        </div>
      </div>
    </section>;
}